// client/src/components/plataforma/siteInstitucional/ConfiguracoesHomePage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useHomeConfig } from '../../../hooks/useHomeConfig';
import { useBanners } from '../../../hooks/useBanners';
import { useContents } from '../../../hooks/useContents';
import { useTestimonials } from '../../../hooks/useTestimonials';
import { useFaqs } from '../../../hooks/useFaqs';
import { useTodasCategorias } from '../../../hooks/useCategorias';
import { useAuth } from '../../../hooks/useAuth';
import useContaPlan from '../../../hooks/useContaPlan';
import useFranquiaPlan from '../../../hooks/useFranquiaPlan';
import { Save, Settings, Loader2, RefreshCcw, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';

import PageHeader from '../../layout/PageHeader';
import LoadingSkeleton from './LoadingSkeleton';
import ThemeSettings from './ThemeSettings';
import SeoSettings from './SeoSettings';
import FixedElementsPanel from './FixedElementsPanel';
import SectionsManager from './SectionsManager';

const DEFAULT_THEME = {
    primaryColor: '#2563eb', secondaryColor: '#10b981', accentColor: '#f59e0b',
    backgroundColor: '#ffffff', textColor: '#1f2937', fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
    borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
};
const DEFAULT_SEO = { title: '', description: '', keywords: [] };

const ConfiguracoesHomePage = () => {
    const {
        homeConfig,
        updateHomeConfig,
        resetHomeConfig,
        loading,
        error,
        addBannerToSection,
        removeBannerFromSection,
        updateSectionBanners,
        reorderSectionBanners,
    } = useHomeConfig();
    
    const { scope: userScope, accountId } = useAuth();
    const contaPlan = useContaPlan();
    const franquiaPlan = useFranquiaPlan();
    // Matriz é dona do template padrão que todo mundo usa — nunca é bloqueada.
    // Mesma feature ('hasHomePersonalizada') existe nos dois planos; qual hook
    // usar depende do escopo logado (mesmo padrão do backend em
    // checkHomePersonalizadaSeNaoMatriz).
    const hasHomePersonalizada = userScope === 'matriz'
        ? true
        : userScope === 'franquia'
            ? franquiaPlan.hasFeature('hasHomePersonalizada')
            : contaPlan.hasFeature('hasHomePersonalizada');
    const { banners, loading: bannersLoading } = useBanners();
    const { contents, loading: contentsLoading } = useContents();
    const { testimonials, loading: testimonialsLoading } = useTestimonials();
    const { faqs, loading: faqsLoading } = useFaqs();
    // Seção 'categorias_vitrine' (conta) — todas as categorias da conta,
    // filtradas por tipo no próprio SectionsManager conforme o admin escolhe
    // o tipo da seção (não dá pra saber o tipo aqui de antemão, pode haver
    // várias seções desse tipo com tipos diferentes cada uma).
    const { data: categoriasVitrineData, isLoading: categoriasLoading } = useTodasCategorias(accountId, { enabled: userScope === 'conta' });
    const [formData, setFormData] = useState(null);
    const [isManagingBanners, setIsManagingBanners] = useState(false);

    useEffect(() => {
        if (homeConfig && typeof homeConfig === 'object') {
            const { _id, __v, ...restConfig } = homeConfig;
            setFormData({
                ...restConfig,
                theme: homeConfig.theme || DEFAULT_THEME,
                seo: homeConfig.seo || DEFAULT_SEO,
                sections: Array.isArray(homeConfig.sections) ? homeConfig.sections : [],
                bannerPositions: Array.isArray(homeConfig.bannerPositions) ? homeConfig.bannerPositions : []
            });
        }
    }, [homeConfig]);

    const activeBanners = useMemo(() => (Array.isArray(banners) ? banners.filter(b => b.status === 'active') : []), [banners]);
    const activeContents = useMemo(() => (Array.isArray(contents) ? contents : []), [contents]);
    const activeTestimonials = useMemo(() => (Array.isArray(testimonials) ? testimonials : []), [testimonials]);
    const activeFaqs = useMemo(() => (Array.isArray(faqs) ? faqs : []), [faqs]);
    const activeFeatures = useMemo(() => activeContents.filter(c => c.type === 'feature' && c.status === 'published'), [activeContents]);
    const activeServices = useMemo(() => activeContents.filter(c => c.type === 'service' && c.status === 'published'), [activeContents]);
    const activeNews = useMemo(() => activeContents.filter(c => c.type === 'news' && c.status === 'published'), [activeContents]);
    const activeUpdates = useMemo(() => activeContents.filter(c => c.type === 'update' && c.status === 'published'), [activeContents]);
    const activeBenefits = useMemo(() => activeContents.filter(c => c.type === 'benefit' && c.status === 'published'), [activeContents]);
    const approvedTestimonials = useMemo(() => activeTestimonials.filter(t => t.status === 'approved'), [activeTestimonials]);
    const publishedFaqs = useMemo(() => activeFaqs.filter(f => f.status === 'published'), [activeFaqs]);
    
    // ✅ FUNÇÃO PARA OBTER BANNERS DISPONÍVEIS (NÃO USADOS EM SEÇÕES)
    const getAvailableBanners = useCallback((currentSectionIndex = null) => {
        if (!activeBanners.length) return [];
        
        const usedBannerIds = new Set();
        
        // Coleta todos os banners usados em outras seções
        if (formData && formData.sections) {
            formData.sections.forEach((section, index) => {
                if (section.type === 'banner' && section.config && section.config.bannerIds && index !== currentSectionIndex) {
                    section.config.bannerIds.forEach(id => usedBannerIds.add(id.toString()));
                }
            });
        }
        
        return activeBanners.filter(banner => !usedBannerIds.has(banner._id.toString()));
    }, [activeBanners, formData]);

    // ✅ FUNÇÃO PARA ADICIONAR BANNER A UMA SEÇÃO
    const handleAddBannerToSection = useCallback(async (sectionIndex, bannerId) => {
        try {
            setIsManagingBanners(true);
            await addBannerToSection(sectionIndex, bannerId);
        } catch {
            // hook já exibe o alerta de erro
        } finally {
            setIsManagingBanners(false);
        }
    }, [addBannerToSection]);

    // ✅ FUNÇÃO PARA REMOVER BANNER DE UMA SEÇÃO
    const handleRemoveBannerFromSection = useCallback(async (sectionIndex, bannerId) => {
        try {
            setIsManagingBanners(true);
            await removeBannerFromSection(sectionIndex, bannerId);
        } catch {
            // hook já exibe o alerta de erro
        } finally {
            setIsManagingBanners(false);
        }
    }, [removeBannerFromSection]);

    // ✅ FUNÇÃO PARA REORDENAR BANNERS EM UMA SEÇÃO
    const handleReorderBanners = useCallback(async (sectionIndex, startIndex, endIndex) => {
        try {
            setIsManagingBanners(true);
            await reorderSectionBanners(sectionIndex, startIndex, endIndex);
        } catch {
            // hook já exibe o alerta de erro
        } finally {
            setIsManagingBanners(false);
        }
    }, [reorderSectionBanners]);

    // ✅ FUNÇÃO PARA ATUALIZAR TODOS OS BANNERS DE UMA SEÇÃO
    const handleUpdateSectionBanners = useCallback(async (sectionIndex, bannerIds) => {
        try {
            setIsManagingBanners(true);
            await updateSectionBanners(sectionIndex, bannerIds);
        } catch {
            // hook já exibe o alerta de erro
        } finally {
            setIsManagingBanners(false);
        }
    }, [updateSectionBanners]);

    const handleFormChange = useCallback((sectionKey, data) => {
        setFormData(prev => ({ ...prev, [sectionKey]: data }));
    }, []);

    const handleReset = async () => {
        if (!hasHomePersonalizada) return;
        if (window.confirm("Você tem certeza que deseja resetar as configurações para o padrão? Esta ação não pode ser desfeita.")) {
            try {
                await resetHomeConfig();
            } catch {
                // hook já exibe o alerta de erro
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData || !hasHomePersonalizada) return;
        const { _id, __v, ...cleanData } = formData;
        try {
            await updateHomeConfig(cleanData);
        } catch {
            // hook já exibe o alerta de erro
        }
    };
    
    if (loading && !formData) return <div className="page-management-container"><LoadingSkeleton /></div>;
    
    if (error && !formData) {
        const errorMessage = typeof error === 'string' ? error : (error?.message || 'Ocorreu um erro desconhecido.');
        return <div className="page-management-container"><div className="alert alert-danger">Erro ao carregar configurações: {errorMessage}</div></div>;
    }

    if (!formData) return <div className="p-6 text-gray-600">Carregando configurações...</div>;

    return (
        <div className="page-management-container">
            <form onSubmit={handleSubmit}>
                <PageHeader
                    title="Configurações da Home"
                    icon={<Settings className="icon-title" />}
                    actions={
                        <>
                            <button
                                type="button"
                                onClick={handleReset}
                                className="action-button action-button-danger"
                                disabled={loading || isManagingBanners || !hasHomePersonalizada}
                            >
                                <RefreshCcw size={16} className="icon" /> Resetar
                            </button>
                            <button
                                type="submit"
                                className="action-button action-button-primary"
                                disabled={loading || isManagingBanners || !hasHomePersonalizada}
                            >
                                {loading || isManagingBanners ? <Loader2 size={16} className="icon animate-spin" /> : <Save size={16} className="icon" />}
                                {isManagingBanners ? 'Gerenciando...' : 'Salvar'}
                            </button>
                        </>
                    }
                />
                {!hasHomePersonalizada && (
                    <div className="content-panel mt-6" style={{ borderLeft: '4px solid #f59e0b', display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Lock size={20} style={{ flexShrink: 0, color: '#f59e0b' }} />
                        <div>
                            <strong>Home personalizada disponível a partir de um plano superior.</strong>
                            <p className="text-sm text-gray-500" style={{ margin: 0 }}>
                                Você está vendo a configuração atual em modo de visualização. Para editar a barra de navegação, seções, tema e demais elementos da home, faça upgrade do seu plano.
                            </p>
                        </div>
                        <Link to="/assinatura?reason=feature" className="action-button action-button-primary" style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                            Ver planos
                        </Link>
                    </div>
                )}
                <div className="content-panel mt-6">
                    <fieldset disabled={!hasHomePersonalizada} style={{ border: 'none', padding: 0, margin: 0, ...(!hasHomePersonalizada ? { opacity: 0.6 } : {}) }}>
                    <ThemeSettings
                        theme={formData.theme}
                        onChange={(newTheme) => handleFormChange('theme', newTheme)}
                    />
                    <SeoSettings
                        seo={formData.seo}
                        onChange={(newSeo) => handleFormChange('seo', newSeo)}
                    />
                    {/* heroConfig só tem efeito no site/conta — o hero de agendamento é
                        conceito exclusivo do app do cliente final. A barra de navegação
                        saiu daqui e virou /site-institucional/navegacao (model NavItem,
                        disponível pra conta/franquia/matriz). */}
                    {userScope === 'conta' && (
                        <FixedElementsPanel
                            heroConfig={formData.heroConfig}
                            onHeroConfigChange={(newHeroConfig) => handleFormChange('heroConfig', newHeroConfig)}
                            disabled={!hasHomePersonalizada}
                        />
                    )}
                    <SectionsManager
                        sections={formData.sections}
                        onChange={(newSections) => handleFormChange('sections', newSections)}
                        userScope={userScope}
                        dataSources={{
                            banners: activeBanners,
                            features: activeFeatures,
                            services: activeServices,
                            news: activeNews,
                            update: activeUpdates,
                            benefit: activeBenefits,
                            testimonials: approvedTestimonials,
                            faqs: publishedFaqs,
                            categorias: categoriasVitrineData?.data || [],
                            loading: { bannersLoading, contentsLoading, testimonialsLoading, faqsLoading, categoriasLoading }
                        }}
                        // ✅ NOVAS PROPS PARA GERENCIAMENTO DE BANNERS
                        bannerManagement={{
                            getAvailableBanners,
                            onAddBanner: handleAddBannerToSection,
                            onRemoveBanner: handleRemoveBannerFromSection,
                            onReorderBanners: handleReorderBanners,
                            onUpdateSectionBanners: handleUpdateSectionBanners,
                            isManaging: isManagingBanners
                        }}
                    />
                    </fieldset>
                </div>
            </form>
        </div>
    );
};

export default ConfiguracoesHomePage;